let inFlightAccessRecovery:
  | Promise<{
      context: {
        accessToken?: string | null
        tenantSlug?: string | null
      }
      reconciliation: 'allowed' | 'no-ticket' | 'unknown'
    }>
  | null = null

async function runSharedAccessRecovery({
  getAccessContext,
  reconcileAccess
}: {
  getAccessContext: () => Promise<{
    accessToken?: string | null
    tenantSlug?: string | null
  }>
  reconcileAccess: (context: {
    accessToken?: string | null
    tenantSlug?: string | null
  }) => Promise<'allowed' | 'no-ticket' | 'unknown'>
}) {
  if (!inFlightAccessRecovery) {
    inFlightAccessRecovery = (async () => {
      const context = await getAccessContext()
      const reconciliation = await reconcileAccess(context)

      return { context, reconciliation }
    })().finally(() => {
      inFlightAccessRecovery = null
    })
  }

  return inFlightAccessRecovery
}

export async function recoverAuthenticatedApiError({
  status,
  bodyStatus,
  error,
  isUserDigitalProductRequest = false,
  getAccessContext,
  reconcileAccess,
  retryRequest,
  handleNoTicket,
  signOut,
  refreshSession,
  // Sem a resposta do pacote, a falha da renovação é tratada como transitória:
  // deslogar por engano revoga a família; não deslogar só falha a requisição.
  isSessionEnded = () => false
}: {
  status?: number | null
  bodyStatus?: number | null
  error: { config?: Record<string, unknown> | null }
  isUserDigitalProductRequest?: boolean
  getAccessContext: () => Promise<{
    accessToken?: string | null
    tenantSlug?: string | null
  }>
  reconcileAccess: (context: {
    accessToken?: string | null
    tenantSlug?: string | null
  }) => Promise<'allowed' | 'no-ticket' | 'unknown'>
  retryRequest: (config: Record<string, unknown>) => Promise<unknown>
  handleNoTicket: (tenantSlug?: string | null) => void
  signOut: () => Promise<void> | void
  /**
   * Renova a sessão pela trava do pacote (`session.refreshCoordinator.refresh`).
   * O access token vive 15 minutos: um 401 durante o uso é, quase sempre, só
   * ele vencido.
   */
  refreshSession?: () => Promise<unknown>
  /**
   * A falha da renovação encerra a sessão? Só esta leva ao `signOut` — que
   * revoga a família na api. Rede ou 5xx falham só a requisição.
   */
  isSessionEnded?: (error: unknown) => boolean
}): Promise<unknown | null | undefined> {
  const authStatus = status ?? bodyStatus

  if (authStatus !== 401 && authStatus !== 403) return undefined

  // Some callers probe a digital-product endpoint just to read state (e.g. "was
  // this invite already accepted?") and must not have a 401/403 silently turned
  // into an access grant — that grant would masquerade as "already accepted"
  // to the caller. They opt out by marking the request config; a stale-token
  // retry is still safe to attempt (it doesn't touch tickets), but access
  // reconciliation (which can silently accept a default-entry invite) is skipped.
  const skipAccessRecovery = Boolean(
    (error.config as Record<string, unknown> | undefined)?._skipAccessRecovery
  )

  // A 401 doesn't always mean the session is actually invalid: a concurrent
  // token refresh (triggered by another in-flight request) can resolve right
  // after this request was sent with a now-stale token. Before signing the
  // user out — and forcing a full page reload that wipes any in-progress UI
  // state — check whether a fresh access token is already available and,
  // if so, retry this request once with it instead of treating the failure
  // as a real auth loss.
  const signOutOrRetryOnceWithFreshSession = async () => {
    const config = error.config

    if (config && !config._sessionRetried && refreshSession) {
      config._sessionRetried = true

      try {
        await refreshSession()
      } catch (refreshError) {
        if (!isSessionEnded(refreshError)) return undefined

        await signOut()
        return null
      }

      return retryRequest(config)
    }

    if (config && !config._sessionRetried) {
      const accessContext = await getAccessContext()

      if (accessContext.accessToken) {
        config._sessionRetried = true
        return retryRequest(config)
      }
    }

    await signOut()
    return null
  }

  if (authStatus === 401 && !isUserDigitalProductRequest) {
    return signOutOrRetryOnceWithFreshSession()
  }

  if (skipAccessRecovery) {
    if (authStatus === 401) return signOutOrRetryOnceWithFreshSession()
    return undefined
  }

  const {
    context: accessContext,
    reconciliation
  } = await runSharedAccessRecovery({
    getAccessContext,
    reconcileAccess
  })

  if (reconciliation === 'no-ticket') {
    handleNoTicket(accessContext.tenantSlug)
    return null
  }

  if (reconciliation === 'allowed') {
    const config = error.config

    if (!config || config._ticketAccessRetried) return null

    config._ticketAccessRetried = true
    return retryRequest(config)
  }

  if (authStatus === 401) {
    return signOutOrRetryOnceWithFreshSession()
  }

  return undefined
}

export function resetAuthRecoveryStateForTests() {
  inFlightAccessRecovery = null
}
